import type { ReactNode } from "react";
import type { Job } from "../../../models";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import type { NearbyJobInfo } from "../../SubmitJobs/Methods/Bulk/wizard/WizardContainer";
import type { MapFeatureData } from "../../SubmitJobs/Methods/Bulk/wizard/steps/DuplicatePointsZones/DuplicatePointsZones.types";

export interface JobMapProps {
	// Data — provide one or the other
	jobs?: Job[];
	job?: Job; // Convenience: equivalent to jobs={[job]}

	// Display
	height?: string | number; // Accepts number (pixels).

	// Coloring:
	//   "layer-type": blue/green/red per layer type
	//   "per-job": hash-based color per job ID
	//   "boundary-hatch": boundary-only with filled polygons + hatch lines (wizard overlapping boundaries)
	//   "boundary-outline": boundary-only with polyline outlines + hatch lines (wizard duplicate boundaries)
	// Default: "per-job"
	colorMode?: "layer-type" | "per-job" | "boundary-hatch" | "boundary-outline";

	// Lasso selection (opt-in)
	lassoMode?: boolean;
	selectedFeatureIds?: Set<string>;
	onLassoComplete?: (selectedIds: Set<string>) => void;

	// Toolbar (opt-in) — when enabled, lasso state is managed internally
	showToolbar?: boolean;
	onClose?: () => void;
	toolbarActions?: ReactNode;

	// Feature selection callback (toolbar mode with lifted state)
	onSelectedFeaturesChange?: (ids: Set<string>) => void;

	// Points visibility — undefined = always show
	zoomLevelToDisplayPoints?: number;

	// Map controls
	disableDefaultUI?: boolean; // Default: false
	zoomControl?: boolean; // Default: true (only relevant when disableDefaultUI=true)

	// Empty/loading states (opt-in)
	showEmptyState?: boolean; // Default: false

	// Bounds padding
	boundsPadding?: google.maps.Padding;

	// Gesture handling (e.g. "cooperative" for wizard maps)
	gestureHandling?: string;

	// ── Wizard overlay props (only used by boundary-hatch / boundary-outline modes) ──

	/** Nearby jobs to display as grayed-out boundaries */
	nearbyJobs?: NearbyJobInfo[];
	/** Unmatched features (points/zones) to overlay on the map */
	unmatchedFeatures?: FeatureCollection;
	/** Duplicate features to overlay (colored point/zone markers) */
	duplicateFeatures?: MapFeatureData[];
	/** Highlight geometry (e.g. intersection area) rendered last on top */
	highlightGeometry?: Feature<Geometry>;

	// Visibility controls for wizard overlays
	hiddenJobIds?: string[];
	hiddenNearbyJobIds?: string[];
	hiddenDuplicateFeatureIds?: Set<string>;
	hideUnmatchedFeatures?: boolean;
}

export interface PopupData {
	job: Job;
	position: google.maps.LatLngLiteral;
}

export interface PolygonMetadata {
	id: string;
	paths: google.maps.LatLngLiteral[];
}

export interface PolygonRenderData {
	id: string;
	paths: google.maps.LatLngLiteral[];
	fillOpacity: number;
	fillColor: string;
	strokeColor: string | undefined;
	strokeWeight?: number;
	strokeOpacity?: number;
	clickable?: boolean;
	job?: Job;
}

export interface PolylineRenderData {
	id: string;
	path: google.maps.LatLngLiteral[];
	strokeColor: string;
	strokeWeight: number;
	strokeOpacity: number;
}

export interface MarkerRenderData {
	id: string;
	position: google.maps.LatLngLiteral;
	iconColor: "error" | "warning" | undefined;
	sampleId: string;
	barcode: string;
	hasChange: boolean;
	changeType: string;
	changeReason: string;
	simpleColor?: string; // For layer-type coloring (colored dot, no info window)
	dotStyle?: {
		size: number;
		color: string;
		border: string;
		shadow: string;
		zIndex?: number;
	};
}

export type ColorMode = "layer-type" | "per-job" | "boundary-hatch" | "boundary-outline";

export interface JobMapViewModel {
	// State
	mapPoints: MarkerRenderData[];
	mapPolygons: PolygonRenderData[];
	visibleMapMarkers: MarkerRenderData[];
	visibleMapPolygons: PolygonRenderData[];
	visiblePolylines: PolylineRenderData[];
	center: google.maps.LatLngLiteral;
	zoom: number;
	bounds: google.maps.LatLngBoundsLiteral | undefined;
	activePopup: PopupData | null;
	mapsLibraryLoaded: boolean;
	polygonMetadata: PolygonMetadata[];

	// Computed from props
	hasGeodata: boolean;
	effectiveJobs: Job[];
	colorMode: ColorMode;
	disableDefaultUI: boolean;
	showEmptyState: boolean;
	zoomControl: boolean;
	resolvedHeight: string | undefined;
	boundsPadding: google.maps.Padding | undefined;
	gestureHandling: string | undefined;

	// Toolbar state
	showToolbar: boolean;
	effectiveLassoMode: boolean;
	effectiveSelectedFeatureIds: Set<string>;
	onToggleLasso: () => void;

	// Handlers
	onBoundsChanged: (bounds: google.maps.LatLngBoundsLiteral) => void;
	onZoomChanged: (zoom: number) => void;
	onMapClick: () => void;
	onInfoWindowClose: () => void;
	onPolygonClick: (job: Job, position: google.maps.LatLngLiteral) => void;
}

export const DEFAULT_CENTER: google.maps.LatLngLiteral = {
	lat: 40.7671815905857,
	lng: -87.16533774444255,
};

export const DEFAULT_ZOOM = 12;

export const LAYER_COLORS = {
	boundary: "#2196f3", // Blue
	zones: "#4caf50", // Green
	points: "#f44336", // Red
} as const;

export const POLYGON_COLORS = [
	"#e51c23",
	"#e91e63",
	"#9c27b0",
	"#673ab7",
	"#3f51b5",
	"#5677fc",
	"#03a9f4",
	"#00bcd4",
	"#009688",
	"#259b24",
	"#8bc34a",
	"#afb42b",
	"#ff9800",
	"#ff5722",
	"#795548",
	"#607d8b",
	"#ffeb3b",
	"#cddc39",
	"#4caf50",
	"#8bc34a",
	"#cddc39",
	"#ffeb3b",
	"#ffc107",
	"#ff9800",
	"#ff5722",
	"#795548",
	"#9e9e9e",
	"#607d8b",
	"#3f51b5",
	"#2196f3",
	"#03a9f4",
	"#00bcd4",
	"#009688",
	"#4caf50",
	"#8bc34a",
	"#cddc39",
	"#ffeb3b",
	"#ffc107",
	"#ff9800",
	"#ff5722",
	"#795548",
	"#9e9e9e",
	"#607d8b",
];
